import {
  TabsContent,
  FormField,
  ToggleGroup,
  ToggleGroupItem,
  BusinessAddressFields,
  InfoTip,
} from '@/components/molecules';
import {
  Button,
  FormSelect,
  Input,
  Paragraph,
  Textarea,
} from '@/components/atoms';
import { useBusiness, useUpdateBusiness } from '@/api';
import { businessIndustries, channel, sales, routes } from '@/lib/constants';
import {
  mapBusinessToAddressFormValues,
  resolveBusinessAddressForApi,
} from '@/lib/helpers/business-address';
import {
  businessUpdateSchema,
  type BusinessUpdateFormData,
} from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Controller,
  useForm,
  type Control,
  type FieldValues,
  type Resolver,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';
import { useEffect } from 'react';
import { Link } from 'react-router';
import CircleWavyCheck from '@/assets/svg/circle-wavy-check.svg?react';

const verificationLabel: Record<string, string> = {
  unverified: 'Not verified',
  pending: 'Verification pending',
  verified: 'Verified',
  failed: 'Verification failed',
};

export const BusinessTab = () => {
  const { data: business, isLoading } = useBusiness();
  const { mutate: updateBusiness, isPending } = useUpdateBusiness();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<BusinessUpdateFormData>({
    resolver: yupResolver(
      businessUpdateSchema
    ) as Resolver<BusinessUpdateFormData>,
    mode: 'onChange',
  });

  const categoryValue = watch('category');

  useEffect(() => {
    if (!business) return;

    const address = mapBusinessToAddressFormValues(business);

    reset({
      name: business.name ?? '',
      category: business.category ?? '',
      description:
        business.description ?? business.submittedData?.description ?? '',
      businessAddress: address.businessAddress,
      state: address.state,
      customState: address.customState ?? '',
      lga: address.lga,
      customLga: address.customLga ?? '',
      country: address.country,
      customCountry: address.customCountry ?? '',
      orderVolume: business.orderVolume ?? '',
      salesChannel: business.salesChannel ?? '',
    });
  }, [business, reset]);

  const onSubmit = (data: BusinessUpdateFormData) => {
    const address = resolveBusinessAddressForApi({
      businessAddress: data.businessAddress ?? '',
      state: data.state ?? '',
      customState: data.customState,
      lga: data.lga ?? '',
      customLga: data.customLga,
      country: data.country ?? '',
      customCountry: data.customCountry,
    });
    updateBusiness({
      name: data.name,
      category: data.category,
      description: data.description,
      orderVolume: data.orderVolume,
      salesChannel: data.salesChannel,
      ...address,
    });
  };

  if (isLoading) {
    return (
      <TabsContent value="business">
        <Paragraph className="text-center py-10 text-muted-foreground">
          Loading business details...
        </Paragraph>
      </TabsContent>
    );
  }

  if (!business) {
    return (
      <TabsContent value="business">
        <div className="flex flex-col items-center gap-4 max-w-xl mx-auto py-10 text-center">
          <Paragraph className="text-muted-foreground">
            No business profile found yet. Complete your business setup to
            manage it here.
          </Paragraph>
          <Button asChild>
            <Link to={routes.kyb}>Complete business setup</Link>
          </Button>
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="business">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 max-w-xl mx-auto py-6 w-full"
      >
        <div className="flex flex-col gap-1">
          {business.verificationStatus === 'verified' ? (
            <div className="flex items-center gap-1.5">
              <CircleWavyCheck className="size-5 shrink-0" aria-hidden />
              <span className="text-sm font-medium text-[#166534]">
                Verified
              </span>
              <InfoTip
                content="Your business has completed KYB verification."
                ariaLabel="About verification status"
              />
            </div>
          ) : (
            <>
              <Paragraph className="text-sm text-muted-foreground">
                Verification status:{' '}
                <span className="font-medium text-foreground">
                  {verificationLabel[business.verificationStatus] ??
                    business.verificationStatus}
                </span>
              </Paragraph>
              <Paragraph className="text-sm">
                <Link to={routes.kyb} className="text-primary underline">
                  Complete KYB verification
                </Link>
              </Paragraph>
            </>
          )}
        </div>

        <FormField label="Business Name" error={errors.name?.message} required>
          <Input placeholder="e.g. Jane" {...register('name')} />
        </FormField>

        <FormField
          label="Business Category"
          error={errors.category?.message}
          required
        >
          <FormSelect
            placeholder="e.g. Don"
            value={categoryValue ?? ''}
            onChange={(value) =>
              setValue('category', value, { shouldValidate: true })
            }
            options={businessIndustries}
          />
        </FormField>

        <FormField
          label="Business Description"
          error={errors.description?.message}
          infoTip="Minimum 100 characters when provided. This helps Sellyt AI understand your business and assist your customers better."
        >
          <Textarea
            placeholder="Tell us what your business does (at least 100 characters)"
            rows={4}
            {...register('description')}
          />
        </FormField>

        <FormField
          label="How many orders do you get weekly?"
          error={errors.orderVolume?.message}
          required
        >
          <Controller
            name="orderVolume"
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type="single"
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                className="grid grid-cols-3 sm:grid-cols-5 gap-2"
              >
                {sales.map((sale) => (
                  <ToggleGroupItem
                    key={sale}
                    value={sale}
                    className="rounded-md px-6 h-11 font-semibold cursor-pointer transition-colors duration-300"
                  >
                    {sale}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          />
        </FormField>

        <BusinessAddressFields
          register={register as unknown as UseFormRegister<FieldValues>}
          control={control as unknown as Control<FieldValues>}
          errors={errors}
          watch={watch as unknown as UseFormWatch<FieldValues>}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        />

        <FormField
          label="Preferred Sales Channel"
          error={errors.salesChannel?.message}
          required
        >
          <Controller
            name="salesChannel"
            control={control}
            render={({ field }) => (
              <ToggleGroup
                type="single"
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                className="grid grid-cols-3 md:grid-cols-4 gap-2"
              >
                {channel.map((item) => (
                  <ToggleGroupItem
                    key={item.value}
                    value={item.value}
                    className="rounded-md px-6 h-11 font-semibold cursor-pointer transition-colors duration-300"
                  >
                    {item.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          />
        </FormField>

        <Button
          type="submit"
          label="Save business details"
          size="lg"
          isLoading={isPending}
          disabled={!isValid || !isDirty}
        />
      </form>
    </TabsContent>
  );
};
